"use client";
import { cn } from "@/lib/utils";
import { useGSAP } from "@gsap/react";
import * as Menubar from "@radix-ui/react-menubar";
import { gsap, Power2 } from "gsap";
import { useEffect, useRef, useState } from "react";
import { GlobalNavClosedPropsModel } from "./GlobalNavClosed.model";

const GlobalNavClosed = (props: GlobalNavClosedPropsModel) => {
  const navBarRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLButtonElement>(null);
  const logoRef = useRef<HTMLAnchorElement>(null);
  const scrollPositionRef = useRef<number>(0);

  const [isVisible, setIsVisible] = useState<boolean>(true);

  const hideOnSrcollDown = props.hideOnSrcollDown;
  useEffect(() => {
    const handleScroll = () => {
      if (
        typeof window !== "undefined" && // ensures the code only runs in environments where window exists
        hideOnSrcollDown
      ) {
        const currentScrollPosition = window.scrollY;
        // Slide up when scrolling down
        if (currentScrollPosition > scrollPositionRef.current) {
          setIsVisible(false);
        } else {
          setIsVisible(true);
        }
        scrollPositionRef.current = currentScrollPosition;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [hideOnSrcollDown]);

  const cls = isVisible ? "onscreen" : "offscreen";

  useGSAP(() => {
    const navBar = navBarRef?.current;
    const icon = iconRef?.current;
    const logo = logoRef?.current;
    const tl = gsap.timeline({
      defaults: { ease: Power2.easeOut },
    });

    tl.from(icon, { x: props.horizontalPosition, duration: 0.2 });
    tl.from(logo, { y: -100, x: 0, duration: 0.2 }, "-=0.2");

    const addAnimation = () => {
      gsap.to(navBar, {
        scaleX: 1.43,
        scaleY: 1.5,
        duration: 0.2,
        ease: "power2.inout",
        onStart: () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          navBar?.children &&
            gsap.to(navBar?.children, {
              duration: 0.2,
              scaleX: 1 / 1.1,
              scaleY: 1 / 1.3,
            });
        },
      });
    };

    const removeAnimation = () => {
      gsap.to(navBar, {
        scale: 1,
        duration: 0.2,
        ease: "power2.inout",
        onStart: () => {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          navBar?.children &&
            gsap.to(navBar?.children, { duration: 0.2, scale: 1 });
        },
      });
    };
    navBar?.addEventListener("mouseenter", addAnimation);
    navBar?.addEventListener("mouseleave", removeAnimation);
    navBar?.addEventListener("focus", addAnimation);
    navBar?.addEventListener("blur", removeAnimation);

    return () => {
      navBar?.removeEventListener("mouseenter", addAnimation);
      navBar?.removeEventListener("mouseleave", removeAnimation);
      navBar?.removeEventListener("focus", addAnimation);
      navBar?.removeEventListener("blur", removeAnimation);
    };
  }, [props.title]);

  return (
    <div
      ref={navBarRef}
      className={cn(
        props.className,
        cls,
        "fixed inset-x-0 top-8 z-50 mx-auto rounded-md outline-none",
      )}
    >
      <div className="flex items-center justify-between">
        <div
          role="button"
          tabIndex={0}
          onKeyDown={event => {
            if (event.key === "Enter" && props.toggleMenu) {
              props.toggleMenu();
            }
          }}
        >
          <Menubar.Root
            className={`right font-JetBrainsRegular flex h-8 w-[102px] min-w-[92px] cursor-pointer items-center justify-between px-3`}
            onClick={props.toggleMenu}
          >
            <Menubar.Menu>
              <Menubar.Trigger
                ref={iconRef}
                className="text-core-white"
                aria-label="Open menu"
              >
                {props.icon}
              </Menubar.Trigger>
            </Menubar.Menu>

            <Menubar.Menu>
              <Menubar.Trigger
                className="text-ll text-core-white font-medium"
                aria-label="Menu"
              >
                {props.title}
              </Menubar.Trigger>
            </Menubar.Menu>
          </Menubar.Root>
        </div>
      </div>
    </div>
  );
};

export default GlobalNavClosed;
